import React, {useMemo, useRef, useState} from 'react';
import {useTranslation} from "react-i18next";
import {useMutation, useQuery} from "@apollo/client";
import _ from "lodash";
import {FormikHelpersRef, TypeDefinitionAttribute} from "../../../types";
import {toast} from "react-toastify";
import Box from "@mui/material/Box";
import {Link, Typography} from "@mui/joy";
import {CreateAssetInput} from "../../../codegen/graphql";
import InstalledBaseTDAForm from "../../InstalledBaseTDAForm";
import ProductNameplate from "../../ProductNameplate";
import {buildAssetInputFromSpecs, KnownSpecs} from "../../../utils/buildAssetInputFromSpecs";
import {CreateAsset} from "../../../graphql/mutations/createAsset";
import {CreateAssetTdas} from "../../../graphql/queries/createAssetTdas";
import {TextField} from "@mui/material";
import {SearchableMultiSelect, CatalogProductNode} from "@dreebit-registry/vsm-microservice-components";
import {resolveErrorMessage} from "../../../utils/resolveErrorMessage";

// SearchableMultiSelect forwards extra props (label, error, helperText) to its
// inner TextField at runtime, but its Props interface doesn't declare them.
const ManufacturerSelect = SearchableMultiSelect as React.ComponentType<any>;

// Moved to utils/buildAssetInputFromSpecs so pure-function tests don't pull in
// the component graph; re-exported here for existing importers.
export {KnownSpecs};

const tdaIndexForSpecs = (specs: KnownSpecs) => {
    switch (specs) {
        case KnownSpecs.PRODUCT_KNOWN:
            return "ASSET_LOCATION_PRODUCT_NODE_ID";
        case KnownSpecs.MANUFACTURER_KNOWN:
            return "ASSET_LOCATION_MANUFACTURER_ID";
        case KnownSpecs.DEFAULT:
            return "ASSET_LOCATION_DEFAULT";
    }
};

// Sentinel inputTypes used to intercept single attributes with customInputRenderers
// (same remap technique as CreateNodeModal/createNodeForm.tsx).
const MANUFACTURER_SELECT_INPUT_TYPE = 'manufacturerSelectWithCreateLink';
const MANUFACTURER_BACK_LINK_INPUT_TYPE = 'manufacturerFieldWithBackLink';

const getAttributeLabel = (attribute: any) => {
    let label = attribute.name;

    if (attribute?.rules?.find((rule: any) => rule.name === "required")) {
        label = label + " *"
    }

    return label
}

export interface InstallationFormProps {
    product?: CatalogProductNode;
    serialNumber?: string;
    /** Prefill from a serial-lookup proposal (systems of record). Flows into the
     *  CreateAsset input even when the TDA set doesn't render a date field. */
    dateOfManufacture?: string;
    knownSpecs: KnownSpecs;
    onAssetReady: (assetId: string) => void;
    submitButtonText?: string;
    /** When true, the internal submit button is visually hidden. The form is still
     *  submitted programmatically via `setFormikHelpersRef`. Used when the form is
     *  embedded in a larger wizard that supplies its own Next button. */
    hideSubmitButton?: boolean;
    setFormikHelpersRef?: (ref: FormikHelpersRef) => void;
}

const RegisterForm = ({product, serialNumber, dateOfManufacture, knownSpecs, onAssetReady, submitButtonText, hideSubmitButton, setFormikHelpersRef}: InstallationFormProps) => {
    const {t} = useTranslation();

    // "Create new manufacturer" mode: swaps the manufacturer select for the
    // free-text name/domain fields of the ASSET_LOCATION_DEFAULT TDA set, and
    // submits the `default` branch of CreateAssetInput instead of `manufacturer`.
    const [createManufacturer, setCreateManufacturer] = useState(false);

    const {data: tdaData} = useQuery(CreateAssetTdas, {
        variables: {
            typeDefinitionIndex: tdaIndexForSpecs(knownSpecs),
        },
    });

    // Prefetched up front so toggling to "create new manufacturer" swaps the
    // manufacturer field(s) in place — no query in the toggle path, the rest of
    // the form stays mounted instead of collapsing while TDAs load.
    const {data: defaultTdaData} = useQuery(CreateAssetTdas, {
        variables: {
            typeDefinitionIndex: tdaIndexForSpecs(KnownSpecs.DEFAULT),
        },
        skip: knownSpecs !== KnownSpecs.MANUFACTURER_KNOWN,
    });

    const [createAsset] = useMutation(CreateAsset);

    // Manufacturer fields of the ASSET_LOCATION_DEFAULT set: manufacturerName,
    // plus manufacturerDomain once the backend ships it.
    const manufacturerFields: TypeDefinitionAttribute[] = useMemo(() => _.get(
        defaultTdaData,
        'assetLocationList.typeDefinitionAttributeList.typeDefinitionAttributes',
        []
    ).filter(
        (attr: any) => attr.index === 'manufacturerName' || attr.index === 'manufacturerDomain'
    ), [defaultTdaData]);

    // Only "in" create mode once the free-text fields are actually renderable —
    // if the prefetch hasn't resolved yet, the form still shows the select and
    // the submit must keep using the `manufacturer` branch.
    const inCreateManufacturerMode = createManufacturer
        && knownSpecs === KnownSpecs.MANUFACTURER_KNOWN
        && manufacturerFields.length > 0;
    const effectiveSpecs = inCreateManufacturerMode ? KnownSpecs.DEFAULT : knownSpecs;

    // Live form values, tracked outside Formik. Splicing the manufacturer fields
    // changes the attribute list and Formik reinitializes (enableReinitialize
    // defaults to true), so on toggle these are merged back into initialFormValues
    // to preserve user input — including manufacturerId, so switching back restores
    // the previous selection. A ref (not state) so keystrokes never recompute
    // initialFormValues.
    const liveValuesRef = useRef<{ [p: string]: any }>({});

    const attributes: TypeDefinitionAttribute[] = useMemo(() => {
        const raw: TypeDefinitionAttribute[] = _.get(
            tdaData,
            'assetLocationList.typeDefinitionAttributeList.typeDefinitionAttributes',
            []
        ).filter(
            // The picked catalog product fixes the productNodeId — don't render the raw
            // id as an editable field; the value is supplied via initialFormValues.
            (attr: TypeDefinitionAttribute) => !(attr.index === 'productNodeId' && product?.id)
        );

        // The manufacturer toggle only exists on the manual path.
        if (knownSpecs !== KnownSpecs.MANUFACTURER_KNOWN) return raw;

        if (!inCreateManufacturerMode) {
            return raw.map((attr: any) =>
                attr.index === 'manufacturerId'
                    ? {...attr, inputType: MANUFACTURER_SELECT_INPUT_TYPE}
                    : attr
            );
        }

        // Create mode: replace the select with the free-text manufacturer fields
        // in place; every other attribute object stays identical, so the rest of
        // the form doesn't remount. The back-link attaches to the last of them.
        const backLinkIndex = manufacturerFields[manufacturerFields.length - 1].index;
        return raw.flatMap((attr: any) =>
            attr.index === 'manufacturerId'
                ? manufacturerFields.map((field: any) => ({
                    ...field,
                    ...(field.index === 'manufacturerDomain' && {placeholder: t("Manufacturer's website address, e.g. buschvacuum.com")}),
                    ...(field.index === backLinkIndex && {inputType: MANUFACTURER_BACK_LINK_INPUT_TYPE}),
                }))
                : [attr]
        );
    }, [tdaData, manufacturerFields, product?.id, knownSpecs, inCreateManufacturerMode, t]);

    const initialFormValues: { [p: string]: any } = useMemo(() => ({
        productId: product?.id,
        productNodeId: product?.id,
        productCode: product?.productCode,
        productName: product?.name,
        manufacturerId: product?.manufacturer?.id,
        manufacturerName: product?.manufacturer?.name,
        serialNumber: serialNumber,
        dateOfManufacture: dateOfManufacture,
        ...liveValuesRef.current,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [product, serialNumber, dateOfManufacture, inCreateManufacturerMode]);

    const customInputRenderers = useMemo(() => ({
        [MANUFACTURER_SELECT_INPUT_TYPE]: ({attribute, value, error, onChange, setFieldValue}: any) => (
            <Box>
                <ManufacturerSelect
                    attribute={attribute}
                    id={attribute.index}
                    name={attribute.index}
                    label={getAttributeLabel(attribute)}
                    placeholder={getAttributeLabel(attribute)}
                    value={value ?? ''}
                    onChange={onChange}
                    error={Boolean(error)}
                    helperText={error}
                    allValues={liveValuesRef.current}
                    setFieldValue={setFieldValue}
                />
                <Typography level={'bodySmall'} sx={{mt: '4px'}}>
                    {t('Manufacturer not listed?')}{' '}
                    {/* type="button": the link renders inside the TDAForm <form>,
                        the default submit type would submit it */}
                    <Link component={'button'} type={'button'} onClick={() => setCreateManufacturer(true)}>
                        {t('Create a new one.')}
                    </Link>
                </Typography>
            </Box>
        ),
        [MANUFACTURER_BACK_LINK_INPUT_TYPE]: ({attribute, value, error, onChange, onBlur}: any) => (
            <Box>
                {/* Visually mirrors TDAForm's stock text branch (TextFieldWithUnit
                    without a unit): fullWidth, small, shrunk label */}
                <TextField
                    id={attribute.index}
                    name={attribute.index}
                    fullWidth
                    size={'small'}
                    label={getAttributeLabel(attribute)}
                    placeholder={attribute.placeholder}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={Boolean(error)}
                    helperText={error}
                    InputLabelProps={{shrink: true}}
                />
                <Typography level={'bodySmall'} sx={{mt: '4px'}}>
                    {t('Manufacturer exists?')}{' '}
                    <Link component={'button'} type={'button'} onClick={() => setCreateManufacturer(false)}>
                        {t('Choose from the list.')}
                    </Link>
                </Typography>
            </Box>
        ),
    }), [t]);

    const handleSubmit = async (values: { [p: string]: any }) => {
        const formValues = {...initialFormValues, ...values};
        try {
            const input: CreateAssetInput = buildAssetInputFromSpecs(effectiveSpecs, formValues);
            const res = await createAsset({variables: {input}});
            const newAssetId = res.data?.createAsset?.id;
            if (!newAssetId) {
                toast.error(t('Error while creating asset.'));
                return;
            }
            toast.success(t('Asset created.'));
            onAssetReady(newAssetId);
        } catch (error: any) {
            toast.error(t('Error while creating asset: ') + resolveErrorMessage(error, t));
        }
    };

    return (
        <Box sx={hideSubmitButton ? { '& button[type="submit"]': { display: 'none' } } : undefined}>
            {product && (
                // Same bordered header box as the asset nameplate in the event wizard
                <Box sx={{mt: 1, mb: 1, mx: 1, p: 2, border: '1px solid #ccc', borderRadius: '5px'}}>
                    <ProductNameplate product={product}/>
                </Box>
            )}
            <InstalledBaseTDAForm
                initialFormValues={initialFormValues}
                formStyle={{padding: '0px 8px 16px 8px'}}
                onSubmit={handleSubmit}
                submitButtonText={submitButtonText ?? t('Next')}
                attributes={attributes}
                setFormikHelpersRef={setFormikHelpersRef}
                onValuesChange={(values) => {
                    liveValuesRef.current = {...liveValuesRef.current, ...values};
                }}
                customInputRenderers={customInputRenderers}
            />
        </Box>
    );
};

export default RegisterForm;
